import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import * as _ from 'lodash-es';
import {CustomerOverViewService} from '~features/customer-overview/customer-overview.service';
import {NotificationType} from '~core/enum';
import {ButtonsTranslations} from '~shared/components/buttons/buttons-translations.service';
import {SpinnerService} from '~shared/components/buttons/spinner/spinner.service';
import {FormLabelTranslationService} from '~shared/components/form-fields/translations/form-label-translations.service';
import {isEmailValid} from '~shared/directives/form-validators/is-email-valid.directive';
import {noWhitespaceCharacters} from '~shared/directives/form-validators';
import {CampaignStatus, Club, Customer} from '~core/models';
import {CustomerService, DefaultNotificationService} from '~core/services';
import {CampaignStatusTranslationsService, CustomerOverviewTranslationsService} from '~shared/translations';


/**
 * Workspace sidebar for editing communication data
 */
@UntilDestroy()
@Component({
  selector: 'app-communication-data',
  templateUrl: './communication-data.component.html',
  styleUrls: ['../../../customer-overview.component.scss']
})
export class CommunicationDataComponent implements OnInit, OnDestroy {
  @Input() public selectedClub: Club;
  public customer: Customer;
  public isEmailValid: boolean = true;
  public spinnerName: string = 'communicationDataUpdate';
  public updateCommunicationForm: UntypedFormGroup;
  public campaignStatus: CampaignStatus;
  public shopAuthorizationLoading: boolean = false;

  constructor(
    private campaignStatusTranslations: CampaignStatusTranslationsService,
    private customerOverviewTranslations: CustomerOverviewTranslationsService,
    private formBuilder: UntypedFormBuilder,
    private notificationService: DefaultNotificationService,
    private customerService: CustomerService,
    private spinnerService: SpinnerService,
    public buttonsTranslations: ButtonsTranslations,
    public flTranslations: FormLabelTranslationService,
    public customerOverviewService: CustomerOverViewService
  ) {
    this.customerService.castSelectedCustomer.pipe(untilDestroyed(this)).subscribe((customer: Customer) => {
      this.customer = customer;
      this.campaignStatus = new CampaignStatus(this.customer.subscriptionStatus, this.campaignStatusTranslations);
    });
  }

  public ngOnInit(): void {
    this.fillCommunicationForm();
  }

  public ngOnDestroy(): void {
  }

  /**
   * Create the communication form.
   */
  public fillCommunicationForm(): void {
    this.updateCommunicationForm = this.formBuilder.group({
      id: [this.customer.id],
      phone: [this.customer.phone],
      mobile: [this.customer.mobile],
      email: [this.customer.email, [
        Validators.required,
        Validators.maxLength(100),
        isEmailValid,
        noWhitespaceCharacters
      ]],
      subscriptionStatus: [this.campaignStatus.status]
    });
  }

  /**
   * If customer form is valid update the customer, otherwise display the errors.
   */
  public updateFormSubmit(form: UntypedFormGroup): void {
    if (form.valid) {
      this.spinnerService.show(this.spinnerName);
      this.customerService.update(form.value)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: (data: Customer) => {
            this.customerService.setSelectedCustomer(data);
            this.notificationService.showNotification({
              type: NotificationType.SUCCESS,
              message: this.customerOverviewTranslations.i18nSuccessCommunicationUpdate
            });
            if (this.customer.isDuplicate) {
              setTimeout(() => {
                location.reload();
              }, 3000);
            } else {
              this.spinnerService.hide(this.spinnerName);
              this.customerOverviewService.toggleAllModal(false);
            }
          },
          error: (error: Error) => {
            this.spinnerService.hide(this.spinnerName);
            this.notificationService.showNotification({
              type: NotificationType.ALERT,
              message: this.customerOverviewTranslations.i18nErrorCommunicationUpdate
            });
          }
        });
    }
  }

  get availableStatuses(): any {
    const statuses = this.campaignStatus.statuses;
    _.remove(statuses, el => el.value === 'bounced');
    return statuses;
  }

  get preselectedValue(): any {
    if (this.campaignStatus.status === 'bounced') {
      return 'subscribed';
    } else {
      return this.campaignStatus.status;
    }
  }

  /**
   * Sets the current email as the authorization email for shop
   */
  public setShopAuthorization(): void {
    this.shopAuthorizationLoading = true;
    this.customerService.setShopAuthorization(this.customer)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (data: Customer) => {
          this.shopAuthorizationLoading = false;
          this.customerService.setSelectedCustomer(data);
          this.customerOverviewService.toggleAllModal(false);
          this.notificationService.showNotification({
            type: NotificationType.SUCCESS,
            message: this.customerOverviewTranslations.i18nSuccessCommunicationUpdate
          });
        },
        error: (err: Error) => {
          this.shopAuthorizationLoading = false;
          this.notificationService.showNotification({
            type: NotificationType.ALERT,
            message: this.customerOverviewTranslations.i18nErrorCommunicationUpdate
          });
        }
      });
  }

}
